import { useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { CircleNotch, CheckCircle } from "@phosphor-icons/react";
import { createLead, EQUIPMENT } from "@/lib/api";

const CONFIG = {
  quote: {
    title: "Get a Freight Quote",
    desc: "Tell us about your shipment and we'll get back within the hour.",
    extra: [
      { name: "origin", label: "Pickup City / State", type: "text" },
      { name: "destination", label: "Delivery City / State", type: "text" },
      { name: "equipment", label: "Equipment", type: "equipment" },
      { name: "commodity", label: "Commodity / Details", type: "textarea" },
    ],
  },
  carrier: {
    title: "Carrier / Driver Signup",
    desc: "Join the Coastal network and start getting loads.",
    extra: [
      { name: "company", label: "Company Name", type: "text" },
      { name: "mc_number", label: "MC Number", type: "text" },
      { name: "dot_number", label: "DOT Number", type: "text" },
      { name: "equipment", label: "Primary Equipment", type: "equipment" },
      { name: "lanes", label: "Preferred Lanes", type: "text" },
    ],
  },
  startup_consult: {
    title: "Start Your Trucking Company",
    desc: "Book a free consult. We handle LLC/EIN, authority, and mentorship.",
    extra: [
      { name: "goal", label: "What's your goal?", type: "textarea" },
      { name: "timeline", label: "Ideal timeline to launch", type: "text" },
    ],
  },
};

export const LeadDialog = ({ open, onOpenChange, type = "quote" }) => {
  const cfg = CONFIG[type];
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({});

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Name and email are required");
      return;
    }
    setLoading(true);
    const payload = {};
    cfg.extra.forEach((f) => {
      if (form[f.name]) payload[f.name] = form[f.name];
    });
    try {
      await createLead({ type, name: form.name, email: form.email, phone: form.phone || "", payload });
      setDone(true);
      toast.success("Received! We'll be in touch shortly.");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const close = (v) => {
    onOpenChange(v);
    if (!v) setTimeout(() => { setDone(false); setForm({}); }, 200);
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="bg-[#111A2E] border-white/10 text-white max-w-lg max-h-[90vh] overflow-y-auto" data-testid={`lead-dialog-${type}`}>
        {done ? (
          <div className="py-10 text-center flex flex-col items-center gap-4" data-testid="lead-success">
            <CheckCircle size={56} weight="fill" className="text-[#2563EB]" />
            <h3 className="font-display text-2xl font-bold">You're all set</h3>
            <p className="text-[#94A3B8] max-w-sm">Our Houston dispatch team received your request and will reach out shortly.</p>
            <button onClick={() => close(false)} className="mt-2 px-6 py-2.5 rounded-full bg-[#2563EB] text-white font-semibold" data-testid="lead-close-btn">Done</button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-display text-2xl tracking-tight">{cfg.title}</DialogTitle>
              <DialogDescription className="text-[#94A3B8]">{cfg.desc}</DialogDescription>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-4 mt-2">
              <div className="grid grid-cols-2 gap-4">
                <Field label="Full Name *"><Input data-testid="lead-name" value={form.name || ""} onChange={(e) => set("name", e.target.value)} className="bg-[#0B1220] border-white/10" /></Field>
                <Field label="Phone"><Input data-testid="lead-phone" value={form.phone || ""} onChange={(e) => set("phone", e.target.value)} className="bg-[#0B1220] border-white/10" /></Field>
              </div>
              <Field label="Email *"><Input type="email" data-testid="lead-email" value={form.email || ""} onChange={(e) => set("email", e.target.value)} className="bg-[#0B1220] border-white/10" /></Field>
              {cfg.extra.map((f) => (
                <Field key={f.name} label={f.label}>
                  {f.type === "textarea" ? (
                    <Textarea data-testid={`lead-${f.name}`} value={form[f.name] || ""} onChange={(e) => set(f.name, e.target.value)} className="bg-[#0B1220] border-white/10" rows={3} />
                  ) : f.type === "equipment" ? (
                    <Select value={form[f.name] || ""} onValueChange={(v) => set(f.name, v)}>
                      <SelectTrigger data-testid={`lead-${f.name}`} className="bg-[#0B1220] border-white/10"><SelectValue placeholder="Select equipment" /></SelectTrigger>
                      <SelectContent className="bg-[#111A2E] border-white/10 text-white">
                        {EQUIPMENT.map((eq) => <SelectItem key={eq} value={eq}>{eq}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input data-testid={`lead-${f.name}`} value={form[f.name] || ""} onChange={(e) => set(f.name, e.target.value)} className="bg-[#0B1220] border-white/10" />
                  )}
                </Field>
              ))}
              <button type="submit" disabled={loading} data-testid="lead-submit-btn" className="w-full py-3 rounded-full bg-[#2563EB] text-white font-semibold hover:bg-[#1D4ED8] transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
                {loading && <CircleNotch size={18} className="animate-spin" />} Submit
              </button>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

const Field = ({ label, children }) => (
  <div className="space-y-1.5">
    <Label className="text-xs text-[#94A3B8]">{label}</Label>
    {children}
  </div>
);
